import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Send, Loader2, Users, CheckCircle, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import RecipientSelector from '../components/RecipientSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CAMPAIGN_TYPES = {
  policy_renewal_reminder: { label: 'Renovación de Póliza', icon: '⏰' },
  receipt_confirmation: { label: 'Confirmación de Pago', icon: '✅' },
  payment_reminder: { label: 'Recordatorio de Pago', icon: '💳' },
  birthday_greeting: { label: 'Cumpleaños', icon: '🎉' },
  portal_access: { label: 'Acceso al Portal', icon: '🔐' },
  special_announcement: { label: 'Comunicación Especial', icon: '✨' }
};

export default function EmailCampaignSend() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [filters, setFilters] = useState({});
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [useManualSelection, setUseManualSelection] = useState(false);

  useEffect(() => {
    fetchCampaign();
  }, [campaignId]);

  const fetchCampaign = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/email-campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const campaignData = response.data.find(c => c.id === campaignId);
      setCampaign(campaignData);
    } catch (error) {
      console.error('Error fetching campaign:', error);
      toast.error('Error al cargar campaña');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    // Validar que haya destinatarios seleccionados si es manual
    if (useManualSelection && selectedRecipients.length === 0) {
      toast.error('Debes seleccionar al menos un destinatario');
      return;
    }

    const recipientCount = useManualSelection ? selectedRecipients.length : 'todos los destinatarios';
    if (!window.confirm(`¿Estás seguro de enviar esta campaña a ${recipientCount}? Esta acción no se puede deshacer.`)) {
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      
      // Preparar el payload según el tipo de selección
      const payload = {
        campaign_id: campaignId,
        schedule_type: 'immediate',
        recipient_filters: useManualSelection ? { manual_ids: selectedRecipients } : filters
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/email-campaigns/${campaignId}/send`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        `${response.data.message}`,
        { duration: 5000 }
      );
      
      // Esperar un momento antes de redirigir
      setTimeout(() => {
        navigate('/email-campaigns/history');
      }, 2000);
    } catch (error) {
      console.error('Error sending campaign:', error);
      const errorMsg = error.response?.data?.detail || 'Error al enviar campaña';
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="p-6">
        <p>Campaña no encontrada</p>
      </div>
    );
  }

  const typeInfo = CAMPAIGN_TYPES[campaign.campaign_type] || {};

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <Button
          onClick={() => navigate('/email-campaigns')}
          variant="ghost"
          className="mb-4 gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Send className="h-8 w-8 text-indigo-600" />
          Enviar Campaña
        </h1>
      </div>

      {/* Campaign Info */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{typeInfo.icon}</span>
              <div>
                <CardTitle>{campaign.name}</CardTitle>
                <CardDescription>{campaign.subject}</CardDescription>
              </div>
            </div>
            <Badge variant="outline" className="text-indigo-600 border-indigo-600">
              {typeInfo.label}
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* Selector de Modo */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Método de Selección
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Button
              variant={!useManualSelection ? 'default' : 'outline'}
              onClick={() => setUseManualSelection(false)}
              className="flex-1 gap-2"
            >
              <Filter className="h-4 w-4" />
              Filtros Automáticos
            </Button>
            <Button
              variant={useManualSelection ? 'default' : 'outline'}
              onClick={() => setUseManualSelection(true)}
              className="flex-1 gap-2"
            >
              <CheckCircle className="h-4 w-4" />
              Selección Manual
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recipients Info - Automático */}
      {!useManualSelection && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Criterios Automáticos
            </CardTitle>
            <CardDescription>
              Se enviará a todos los clientes que cumplan con los criterios de esta campaña
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {campaign.campaign_type === 'policy_renewal_reminder' && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-900">
                    <strong>📋 Criterio:</strong> Clientes con pólizas que vencen en los próximos 30 días
                  </p>
                </div>
              )}
              
              {campaign.campaign_type === 'birthday_greeting' && (
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-900">
                    <strong>🎂 Criterio:</strong> Clientes con cumpleaños hoy
                  </p>
                </div>
              )}
              
              {campaign.campaign_type === 'payment_reminder' && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-900">
                    <strong>💳 Criterio:</strong> Clientes con pagos pendientes
                  </p>
                </div>
              )}

              {['portal_access', 'special_announcement', 'receipt_confirmation'].includes(campaign.campaign_type) && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-900">
                    <strong>📧 Criterio:</strong> Todos los clientes con email registrado
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selector Manual de Destinatarios */}
      {useManualSelection && (
        <div className="mb-6">
          <RecipientSelector
            onSelectionChange={setSelectedRecipients}
            campaignType={campaign.campaign_type}
          />
        </div>
      )}

      {/* Send Button */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-lg mb-1">¿Listo para enviar?</h3>
              <p className="text-sm text-gray-600">
                Los emails se enviarán inmediatamente a todos los destinatarios
              </p>
            </div>
            
            <Button
              onClick={handleSend}
              disabled={sending || (useManualSelection && selectedRecipients.length === 0)}
              size="lg"
              className="gap-2"
            >
              {sending ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5" />
                  {useManualSelection 
                    ? `Enviar a ${selectedRecipients.length} destinatario(s)`
                    : 'Enviar Ahora'
                  }
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Warning */}
      <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
        <p className="text-sm text-yellow-800">
          ⚠️ <strong>Importante:</strong> Una vez enviados, los emails no se pueden cancelar. 
          Asegúrate de revisar el contenido antes de confirmar el envío.
        </p>
      </div>
    </div>
  );
}
